import { z } from 'zod';
import { entityBase, id, organizationId, timestamp } from './common.js';

/** The four professional roles captured in v1. */
export const profession = z.enum(['hygienist', 'assistant', 'front_desk', 'dentist']);
export type Profession = z.infer<typeof profession>;

export const professionalStatus = z.enum(['draft', 'pending', 'active', 'suspended']);
export type ProfessionalStatus = z.infer<typeof professionalStatus>;

export const professional = entityBase.extend({
  /** Links to a platform user (auth/identity lives in the platform, not here). */
  userId: id,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  profession,
  yearsExperience: z.number().int().nonnegative().nullable(),
  bio: z.string().nullable(),
  phone: z.string().nullable(),
  preferredRadiusMiles: z.number().int().nonnegative().nullable(),
  homePostalCode: z.string().nullable(),
  homeLatitude: z.number().nullable(),
  homeLongitude: z.number().nullable(),
  /** IANA timezone used to interpret recurring and date-specific availability. */
  availabilityTimezone: z.string().min(1),
  status: professionalStatus,
});
export type Professional = z.infer<typeof professional>;

/** Privacy-minimized professional summary shown to an authorized office team manager. */
export const officeTeamMember = z.object({
  id,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  profession,
  yearsExperience: z.number().int().nonnegative().nullable(),
  phone: z.string().nullable(),
  status: professionalStatus,
  createdAt: timestamp,
  credentialSummary: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
    missingDocument: z.number().int().nonnegative(),
  }),
});
export type OfficeTeamMember = z.infer<typeof officeTeamMember>;

export const createProfessionalInput = z.object({
  organizationId,
  userId: id,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  profession,
  yearsExperience: z.number().int().nonnegative().nullish(),
  bio: z.string().nullish(),
  phone: z.string().nullish(),
  preferredRadiusMiles: z.number().int().nonnegative().nullish(),
  homePostalCode: z.string().nullish(),
  homeLatitude: z.number().nullish(),
  homeLongitude: z.number().nullish(),
  availabilityTimezone: z.string().min(1).default('UTC'),
});
export type CreateProfessionalInput = z.infer<typeof createProfessionalInput>;

/** Self-service profile patch (professionals/me). */
export const updateProfessionalProfileInput = z
  .object({
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    profession: profession.optional(),
    yearsExperience: z.number().int().nonnegative().nullish(),
    bio: z.string().nullish(),
    phone: z.string().nullish(),
    preferredRadiusMiles: z.number().int().nonnegative().nullish(),
    homePostalCode: z.string().nullish(),
    homeLatitude: z.number().min(-90).max(90).nullish(),
    homeLongitude: z.number().min(-180).max(180).nullish(),
    availabilityTimezone: z.string().min(1).optional(),
  })
  .superRefine((input, context) => {
    const latitudeSupplied = input.homeLatitude !== undefined;
    const longitudeSupplied = input.homeLongitude !== undefined;
    if (
      latitudeSupplied !== longitudeSupplied ||
      (latitudeSupplied && (input.homeLatitude === null) !== (input.homeLongitude === null))
    ) {
      context.addIssue({
        code: 'custom',
        path: ['homeLatitude'],
        message: 'Supply both location coordinates, or clear both',
      });
    }
  });
export type UpdateProfessionalProfileInput = z.infer<typeof updateProfessionalProfileInput>;

/** Normalized skill for matching/discovery filters. */
export const professionalSkill = z.object({
  id,
  organizationId,
  professionalId: id,
  skill: z.string().min(1),
  createdAt: timestamp,
});
export type ProfessionalSkill = z.infer<typeof professionalSkill>;

/**
 * Full-calendar availability: recurring weekly rules (dayOfWeek) AND specific-date entries
 * (specificDate), each markable available or unavailable (blackout).
 */
export const availabilityKind = z.enum(['recurring', 'date']);
export type AvailabilityKind = z.infer<typeof availabilityKind>;

export const availabilityStatus = z.enum(['available', 'unavailable']);
export type AvailabilityStatus = z.infer<typeof availabilityStatus>;

export const availability = entityBase.extend({
  professionalId: id,
  kind: availabilityKind,
  /** 0=Sunday … 6=Saturday, for `recurring`; null for `date`. */
  dayOfWeek: z.number().int().min(0).max(6).nullable(),
  /** ISO date (YYYY-MM-DD) for `date`; null for `recurring`. */
  specificDate: z.string().nullable(),
  /** HH:MM (24h) local to the professional. */
  startTime: z.string(),
  endTime: z.string(),
  status: availabilityStatus,
  notes: z.string().nullable(),
});
export type Availability = z.infer<typeof availability>;

export const createAvailabilityInput = z
  .object({
    organizationId,
    professionalId: id,
    kind: availabilityKind,
    dayOfWeek: z.number().int().min(0).max(6).nullish(),
    specificDate: z.string().nullish(),
    startTime: z.string(),
    endTime: z.string(),
    status: availabilityStatus.default('available'),
    notes: z.string().nullish(),
  })
  .refine((a) => (a.kind === 'recurring' ? a.dayOfWeek != null : a.specificDate != null), {
    message: 'recurring requires dayOfWeek; date requires specificDate',
  });
export type CreateAvailabilityInput = z.infer<typeof createAvailabilityInput>;

/** Explicit weekly replacement: malformed requests must never clear a professional's schedule. */
export const recurringAvailabilitySlot = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6),
    startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a valid start time'),
    endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a valid end time'),
  })
  .refine((slot) => slot.startTime < slot.endTime, {
    message: 'End time must be after start time',
    path: ['endTime'],
  });
export type RecurringAvailabilitySlot = z.infer<typeof recurringAvailabilitySlot>;

export const replaceWeeklyAvailabilityInput = z.object({
  slots: z.array(recurringAvailabilitySlot).max(98, 'Use no more than 98 weekly intervals'),
});

const overrideDateValue = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a valid date')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
  }, 'Use a real calendar date');
export const dateOverrideSnapshot = z.object({
  specificDate: overrideDateValue,
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  status: availabilityStatus,
  notes: z.string().nullable(),
});
export const updateDateOverrideInput = dateOverrideSnapshot
  .extend({ notes: z.string().max(2000).nullable(), expected: dateOverrideSnapshot })
  .strict()
  .refine((value) => value.startTime < value.endTime, {
    path: ['endTime'],
    message: 'End time must be after start time',
  });
export type UpdateDateOverrideInput = z.infer<typeof updateDateOverrideInput>;
